import { useEffect, useState } from "react";
import { db } from "../../../firebase/firestore";
import { collection, onSnapshot } from "firebase/firestore";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { logError, logSuccess, getErrorMessage } from "../../../utils/errorHandler";

function toMillis(value) {
  if (!value) return Date.now();
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : Date.now();
  }
  if (typeof value?.toDate === "function") {
    return value.toDate().getTime();
  }
  if (Number.isFinite(value?.seconds)) {
    return Number(value.seconds) * 1000;
  }
  return Date.now();
}

function getOrderAmount(order) {
  return Number(order?.payableTotal ?? order?.pricing?.total ?? order?.total ?? 0) || 0;
}

function isCancelled(order) {
  return String(order?.status || "").toLowerCase() === "cancelled";
}

export default function Analytics() {
  const [total, setTotal] = useState(0);
  const [orders, setOrders] = useState(0);
  const [data, setData] = useState([]);
  const [profit, setProfit] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const hasChartData = data.length > 0;

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 0,
    }).format(value || 0);

  useEffect(() => {
    // 🔥 REAL-TIME LISTENER WITH ERROR HANDLING
    let unsub;
    try {
      unsub = onSnapshot(
        collection(db, "orders"),
        (snap) => {
          try {
            let totalSales = 0;
            let activeOrderCount = 0;

            const daily = {};

            snap.docs.forEach((doc) => {
              const d = doc.data();
              if (isCancelled(d)) return;

              activeOrderCount += 1;
              const amount = getOrderAmount(d);
              totalSales += amount;

              // 📅 group by date (supports Firestore Timestamp and ISO string)
              const timestamp = toMillis(d.createdAt || d.createdAtMs);
              const date = new Date(timestamp).toLocaleDateString("en-GB");

              if (!daily[date]) {
                daily[date] = 0;
              }

              daily[date] += amount;
            });

            // convert to chart format (sorted by date asc)
            const chartData = Object.keys(daily)
              .map((date) => ({
                date,
                sales: daily[date],
                timestamp: toMillis(date),
              }))
              .sort((a, b) => a.timestamp - b.timestamp)
              .map(({ date, sales }) => ({ date, sales }));

            // 🔥 profit calculation (example 30% margin)
            const profitValue = totalSales * 0.3;

            setTotal(totalSales);
            setOrders(activeOrderCount);
            setData(chartData);
            setProfit(profitValue);
            setError("");
            setLoading(false);
            logSuccess("Analytics", "Data loaded successfully");
          } catch (err) {
            logError("Analytics Snapshot", err);
            setError("Failed to process analytics data");
            setLoading(false);
          }
        },
        (error) => {
          logError("Analytics Listener", error);
          setError(getErrorMessage(error));
          setLoading(false);
        }
      );
    } catch (err) {
      logError("Analytics Setup", err);
      setError(getErrorMessage(err));
      setLoading(false);
    }

    return () => {
      if (unsub) unsub();
    };
  }, []);

  return (
    <div className="analytics-container">

      {/* 🔥 ERROR DISPLAY */}
      {error && (
        <div className="error-banner">
          ⚠️ {error}
        </div>
      )}

      {/* 🔥 LOADING STATE */}
      {loading ? (
        <div className="card" style={{ textAlign: "center", padding: "40px" }}>
          ⏳ Loading analytics data...
        </div>
      ) : (
        <>
          {/* 🔥 STATS */}
          <div className="stats-grid">
        <div className="card">
          <h3>Total Sales</h3>
          <h2>₹{formatCurrency(total)}</h2>
        </div>

        <div className="card">
          <h3>Total Orders</h3>
          <h2>{orders}</h2>
        </div>

        <div className="card">
          <h3>Profit (30%)</h3>
          <h2>₹{formatCurrency(profit)}</h2>
        </div>
      </div>

      {/* 🔥 LINE CHART */}
      <div className="chart-box">
        <h3>📈 Daily Sales</h3>

        {hasChartData ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={20} />
              <YAxis />
              <Tooltip formatter={(value) => [`₹${formatCurrency(value)}`, "Sales"]} />
              <Line type="monotone" dataKey="sales" stroke="#ff7a00" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="chart-empty">No delivered sales data yet.</div>
        )}
      </div>

      {/* 🔥 BAR CHART */}
      <div className="chart-box">
        <h3>📊 Sales Comparison</h3>

        {hasChartData ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={20} />
              <YAxis />
              <Tooltip formatter={(value) => [`₹${formatCurrency(value)}`, "Sales"]} />
              <Bar dataKey="sales" fill="#ffb547" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="chart-empty">No delivered sales data yet.</div>
        )}
      </div>
        </>
      )}
    </div>
  );
}